import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import type { QuestionWithEvaluation } from "@services/evaluationService";
import L1EvaluationCard from "./L1EvaluationCard";
import SMEEvaluationForm, { type SMEFormData } from "./SMEEvaluationForm";
import styles from "./EvaluationCard.module.css";
import L1SMEEvaluationForm, { type SMEFormData as L1SMEFormData } from "./L1SMEEvaluationForm";

type EvaluationVariant = "L1Evaluated" | "SMEEvaluation" | "StudentChallenged" | "L1SMEEvaluation";

interface EvaluationContainerProps {
  question: QuestionWithEvaluation;
  onApprovalValidityChange: (isValid: boolean) => void;
  onFormDataChange?: (
    data: any,
    variant: EvaluationVariant
  ) => void;
}

function determineVariant(question: QuestionWithEvaluation): EvaluationVariant {
  // StudentChallenged: student_comment is available (not in current API structure, but keeping for future)
  // For now, we'll use L1Evaluated as default since the API doesn't include student_comment
  
  // If current_evaluation exists, show SMEEvaluation form (pre-filled, read-only)
  if (question.previous_evaluation_result && question.current_evaluation) {
    return "SMEEvaluation";
  }

  // L1Evaluated: previous_evaluation_result is available and no student_comment
  if (question.previous_evaluation_result) {
    return "L1Evaluated";
  }

  // Default to L1Evaluated (this shouldn't happen based on requirements)
  return "L1SMEEvaluation";
}

export default function EvaluationContainer({
  question,
  onApprovalValidityChange,
  onFormDataChange
}: EvaluationContainerProps) {
  const initialVariant = determineVariant(question);

  const [currentVariant, setCurrentVariant] =
    useState<EvaluationVariant>(initialVariant);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isOverrideMode, setIsOverrideMode] = useState(false);


  // Update variant when question changes
  useEffect(() => {
    const newVariant = determineVariant(question);
    setCurrentVariant(newVariant);
    // If current_evaluation exists, this question is already evaluated
    if (question.current_evaluation) {
      setIsAccepted(true);
      setIsOverrideMode(false);
      onApprovalValidityChange(true);
      onFormDataChange?.(
        {
          marks: String(question.current_evaluation.awarded_marks),
          remarks: question.current_evaluation.feedbacks.join(" ") || "",
          verdict: question.current_evaluation.evaluation_verdict || "",
        },
        "SMEEvaluation"
      );
    } else {
      setIsAccepted(false);
      setIsOverrideMode(false);
      onApprovalValidityChange(
        newVariant !== "StudentChallenged" &&
          newVariant !== "L1Evaluated" &&
          newVariant !== "L1SMEEvaluation"
      );
      onFormDataChange?.({ marks: "", remarks: "", verdict: "" }, newVariant);
    }
  }, [question.question.id, onApprovalValidityChange]);

  const handleAccept = useCallback(() => {
    setIsAccepted(true);
    onApprovalValidityChange(true);
  
    // Format the data properly for the parent component
    if (question?.previous_evaluation_result) {
      const formattedData = {
        marksAwarded: question.previous_evaluation_result.awarded_marks,
        feedback: question.previous_evaluation_result.feedbacks.join(", "),
        marks: question.previous_evaluation_result.awarded_marks.toString(),
        remarks: question.previous_evaluation_result.feedbacks.join(", "),
        verdict: question.previous_evaluation_result.evaluation_verdict
      };
      
      onFormDataChange?.(formattedData, "L1Evaluated");
    } else {
      console.warn("EvaluationContainer - No previous evaluation result available");
    }
  }, [onApprovalValidityChange, onFormDataChange, question]);
  

  const handleOverride = useCallback(() => {
    setCurrentVariant("SMEEvaluation");
    setIsOverrideMode(true);
    // When entering override mode, approval is disabled until form is valid
    onApprovalValidityChange(false);
  }, [onApprovalValidityChange]);

  const handleCancel = useCallback(() => {
    // If we have current_evaluation, go back to read-only SME form
    if (question.current_evaluation) {
      setIsOverrideMode(false);
      onFormDataChange?.(
        {
          marks: String(question.current_evaluation.awarded_marks),
          remarks: question.current_evaluation.feedbacks.join(" ") || "",
          verdict: question.current_evaluation.evaluation_verdict || "",
        },
        "SMEEvaluation"
      );
      onApprovalValidityChange(true);
    } else {
      setCurrentVariant("L1Evaluated");
      onApprovalValidityChange(isAccepted);

      onFormDataChange?.(
        {
          marks: question.previous_evaluation_result?.awarded_marks?.toString() || "",
          remarks: question.previous_evaluation_result?.feedbacks?.join("\n") || "",
          verdict: question.previous_evaluation_result?.evaluation_verdict || "",
        },
        "L1Evaluated"
    );
  }}, [question?.current_evaluation, isAccepted, onApprovalValidityChange, onFormDataChange, question]);
    

  const handleSMEFormChange = useCallback(
    (data: SMEFormData, isValid: boolean) => {
      onApprovalValidityChange(isValid);
      onFormDataChange?.(data, "SMEEvaluation");
    },
    [onApprovalValidityChange, onFormDataChange]
  );

  const handleL1SMEFormChange = useCallback((data: L1SMEFormData, isValid: boolean) => {
    onApprovalValidityChange(isValid);
    onFormDataChange?.(data, "L1SMEEvaluation");
  }, [onApprovalValidityChange, onFormDataChange]);

  // Spring animation configuration
  const springConfig = {
    type: "spring" as const,
    stiffness: 300,
    damping: 25,
    mass: 0.8,
  };

  // Variant-specific animation directions
  const getVariantAnimation = (variant: EvaluationVariant) => {
    const baseVariants = {
      initial: { opacity: 0, scale: 0.9 },
      animate: {
        opacity: 1,
        scale: 1,
        transition: {
          ...springConfig,
          stiffness: 320,
          damping: 22,
        },
      },
      exit: {
        opacity: 0,
        scale: 0.9,
        transition: {
          ...springConfig,
          stiffness: 400,
          damping: 28,
        },
      },
    };

    // Different entry directions
    switch (variant) {
      case "L1Evaluated":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, x: -30, rotateY: -5 },
          animate: {
            ...baseVariants.animate,
            x: 0,
            rotateY: 0,
          },
          exit: {
            ...baseVariants.exit,
            x: 30,
            rotateY: 5,
          },
        };
      case "SMEEvaluation":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, y: 30, rotateX: -5 },
          animate: {
            ...baseVariants.animate,
            y: 0,
            rotateX: 0,
          },
          exit: {
            ...baseVariants.exit,
            y: -30,
            rotateX: 5,
          },
        };
      case "StudentChallenged":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, x: 30, rotateY: 5 },
          animate: {
            ...baseVariants.animate,
            x: 0,
            rotateY: 0,
          },
          exit: {
            ...baseVariants.exit,
            x: -30,
            rotateY: -5,
          },
        };
      case "L1SMEEvaluation":
        return {
          ...baseVariants,
          initial: { ...baseVariants.initial, y: 30, rotateX: -5 },
          animate: {
            ...baseVariants.animate,
            y: 0,
            rotateX: 0,
          },
          exit: {
            ...baseVariants.exit,
            y: -30,
            rotateX: 5,
          },
        };
      default:
        return baseVariants;
    }
  };

  const renderContent = () => {
    const contentKey = `${currentVariant}-${question.question.id}`;

    switch (currentVariant) {
      case "L1Evaluated":
        if (!question.previous_evaluation_result) {
          return null;
        }
        return (
          <motion.div
            key={contentKey}
            variants={getVariantAnimation("L1Evaluated")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <L1EvaluationCard
              result={question.previous_evaluation_result}
              maxMarks={question.question.max_marks}
              onAccept={handleAccept}
              onOverride={handleOverride}
              isAccepted={isAccepted}
            />
          </motion.div>
        );

      case "SMEEvaluation":
        return (
          <motion.div
            key={contentKey}
            variants={getVariantAnimation("SMEEvaluation")}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <SMEEvaluationForm
              currentEvaluation={question.current_evaluation}
              previousResult={question.previous_evaluation_result}
              isOverrideMode={isOverrideMode}
              minMarks={question.question.min_marks}
              maxMarks={question.question.max_marks}
              onOverride={handleOverride}
              onCancel={handleCancel}
              onChange={handleSMEFormChange}
            />
          </motion.div>
        );

      case "StudentChallenged":
        // Note: student_comment is not in current API structure
        // This case is kept for future compatibility
        return null;

      case "L1SMEEvaluation":
        return (<motion.div
          key={contentKey}
          variants={getVariantAnimation("L1SMEEvaluation")}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <L1SMEEvaluationForm
            currentEvaluation={question.current_evaluation}
            minMarks={question.question.min_marks}
            maxMarks={question.question.max_marks}
            onChange={handleL1SMEFormChange}
          />
        </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springConfig}
    >
      <motion.div
        className={styles.header}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ ...springConfig, delay: 0.1 }}
      >
        <h2 className={styles.title}>Evaluation & Approval</h2>
      </motion.div>
      <motion.div
        className={styles.divider}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ ...springConfig, delay: 0.15 }}
      />
      <div className={styles.contentSection}>
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </div>
    </motion.div>
  );
}
